import { useEffect, useState } from 'react'
import { Box, Flex, VStack, Grid } from '@chakra-ui/react'
import { LuCheck, LuX, LuCircleCheck } from 'react-icons/lu'
import { DataTable, DataList, Modal, PageHeader, Text, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { conciliacionService } from '../../api/services/conciliacion.service'
import { usePagedRequest } from '../../hooks'
import type {
  ReconciliationDetailResponse,
  ReconciliationListItemResponse,
} from '../../api/models/response'

const formatNumber = (v: number | null | undefined, digits = 2) =>
  v == null ? '-' : v.toLocaleString('es-PE', { minimumFractionDigits: digits, maximumFractionDigits: digits })

const DECISION_LABELS: Record<string, { label: string; color: string }> = {
  Pendiente: { label: 'PENDIENTE', color: '#EAB308' },
  Aceptada: { label: 'ACEPTADA', color: '#2a8a87' },
  Rechazada: { label: 'RECHAZADA', color: '#c8442f' },
}

export default function ConciliacionPage() {
  const colors = themeColors
  const [search, setSearch] = useState('')
  const [decisionFilter, setDecisionFilter] = useState<'' | 'Pendiente' | 'Aceptada' | 'Rechazada'>('Pendiente')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<ReconciliationDetailResponse | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)

  const {
    items,
    totalCount,
    isFetching: loading,
    error,
    refresh,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
  } = usePagedRequest(conciliacionService.getAll, {
    params: {
      search: search.trim() || undefined,
      decision: decisionFilter || undefined,
    },
  })

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }
    setDetailLoading(true)
    conciliacionService
      .getDetail(selectedId)
      .then(setDetail)
      .finally(() => setDetailLoading(false))
  }, [selectedId])

  const closeDetail = () => {
    setSelectedId(null)
    setDetail(null)
    setRejectModalOpen(false)
    setRejectReason('')
  }

  const handleAceptar = async () => {
    if (!selectedId) return
    try {
      setSubmitting(true)
      await conciliacionService.aceptar(selectedId)
      setSuccessDialogOpen(true)
      refresh()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error aceptando conciliacion'
      toaster.error({ description: message })
    } finally {
      setSubmitting(false)
    }
  }

  const handleRechazar = async () => {
    if (!selectedId || !rejectReason.trim()) {
      toaster.error({ description: 'Ingrese motivo de rechazo.' })
      return
    }
    try {
      setSubmitting(true)
      await conciliacionService.rechazar(selectedId, { rejectionReason: rejectReason.trim() })
      toaster.success({ description: 'Carga rechazada.' })
      refresh()
      closeDetail()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error rechazando conciliacion'
      toaster.error({ description: message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title="Conciliación"
        subtitle="Decisión comercial sobre los lotes analizados en laboratorio"
        colors={colors}
      />

      <Flex gap={2} flexWrap="wrap">
        {(['', 'Pendiente', 'Aceptada', 'Rechazada'] as const).map((d) => (
          <Box
            as="button"
            key={d || 'all'}
            px={3}
            py={1.5}
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
            cursor="pointer"
            bg={decisionFilter === d ? brand.primary : '#efe8dd'}
            color={decisionFilter === d ? 'white' : '#4f4842'}
            onClick={() => setDecisionFilter(d)}
          >
            {d === '' ? 'TODAS' : DECISION_LABELS[d]?.label ?? d}
          </Box>
        ))}
      </Flex>

      <DataList<ReconciliationListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por lote o proveedor..."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        entityName="lotes"
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        brandColor={brand.green}
        headers={['Lote', 'Proveedor', 'Humedad', 'Minerales', 'Decisión']}
        data={items}
        loading={loading}
        error={error}
        emptyMessage="No hay lotes para conciliar"
        loadingMessage="Cargando..."
        colors={colors}
        keyExtractor={(r) => r.shipmentRecordId}
        renderRow={(row) => {
          const di = DECISION_LABELS[row.decision] ?? DECISION_LABELS.Pendiente
          return (
            <>
              <DataTable.Cell>
                <Text
                  fontFamily="'JetBrains Mono', monospace"
                  fontSize="sm"
                  fontWeight="700"
                  color={colors.text}
                >
                  {row.batchCode}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <VStack gap={0} alignItems="flex-start">
                  <Text fontSize="sm" color={colors.text}>
                    {row.supplierName}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {row.supplierRuc}
                  </Text>
                </VStack>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatNumber(row.moisture, 1)}%
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Flex gap={1} flexWrap="wrap">
                  {row.minerals.slice(0, 3).map((m, i) => (
                    <Box
                      key={i}
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      fontSize="10px"
                      bg="#efe8dd"
                      color="#4f4842"
                      fontWeight="600"
                    >
                      {m.code} {formatNumber(m.grade, 2)} {m.unit}
                    </Box>
                  ))}
                  {row.minerals.length > 3 && (
                    <Text fontSize="10px" color={colors.textMuted}>
                      +{row.minerals.length - 3}
                    </Text>
                  )}
                </Flex>
              </DataTable.Cell>
              <DataTable.Cell>
                <Box
                  display="inline-block"
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  fontSize="10px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                  bg={`${di.color}33`}
                  color={di.color}
                >
                  {di.label}
                </Box>
              </DataTable.Cell>
            </>
          )
        }}
        actions={(row) => [
          {
            label: row.decision === 'Pendiente' ? 'Conciliar' : 'Ver Detalle',
            onClick: () => setSelectedId(row.shipmentRecordId),
          },
        ]}
      />

      {/* Modal de detalle / decisión */}
      <Modal open={!!selectedId} onClose={closeDetail} title="Resultados de Laboratorio">
        {detailLoading && (
          <Text fontSize="sm" color={colors.textMuted} textAlign="center" py={8}>
            Cargando detalle...
          </Text>
        )}
        {!detailLoading && detail && (
          <VStack gap={4} alignItems="stretch">
            <Box>
              <Text fontSize="lg" fontWeight="700" color={brand.primary}>
                {detail.supplierName}
              </Text>
              <Text fontSize="xs" color={colors.textMuted} fontFamily="'JetBrains Mono', monospace">
                {detail.batchCode}
              </Text>
            </Box>

            <Box bg="#faf6f0" borderRadius="lg" p={4}>
              <Text fontSize="xs" fontWeight="700" color={colors.textMuted} mb={3} letterSpacing="0.06em">
                RESULTADOS DE LABORATORIO
              </Text>
              <Grid templateColumns={`repeat(${Math.min(detail.readings.length + 1, 4)}, 1fr)`} gap={3}>
                <Box bg="white" borderRadius="md" p={3}>
                  <Text fontSize="10px" color={colors.textMuted} fontWeight="700">
                    HUMEDAD
                  </Text>
                  <Text fontSize="lg" fontWeight="700" color={colors.text}>
                    {formatNumber(detail.moisture, 2)}%
                  </Text>
                </Box>
                {detail.readings.map((r, i) => (
                  <Box key={i} bg="white" borderRadius="md" p={3}>
                    <Text fontSize="10px" color={colors.textMuted} fontWeight="700">
                      {r.mineralName.toUpperCase()} ({r.mineralCode})
                    </Text>
                    <Flex alignItems="baseline" gap={1}>
                      <Text fontSize="lg" fontWeight="700" color={colors.text}>
                        {formatNumber(r.grade, 3)}
                      </Text>
                      <Text fontSize="xs" color={colors.textMuted}>
                        {r.unit}
                      </Text>
                    </Flex>
                  </Box>
                ))}
              </Grid>
            </Box>

            {detail.decision === 'Pendiente' && (
              <Box>
                <Text fontSize="sm" fontWeight="700" textAlign="center" mb={3} color={colors.text}>
                  Decisión Comercial
                </Text>
                <Grid templateColumns="1fr 1fr" gap={3}>
                  <button
                    type="button"
                    onClick={() => setRejectModalOpen(true)}
                    disabled={submitting}
                    style={{
                      padding: '16px',
                      borderRadius: 10,
                      border: '2px solid #c8442f',
                      background: 'white',
                      color: '#c8442f',
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    <LuX size={20} /> Rechazar Carga
                  </button>
                  <button
                    type="button"
                    onClick={handleAceptar}
                    disabled={submitting}
                    style={{
                      padding: '16px',
                      borderRadius: 10,
                      border: 'none',
                      background: brand.primary,
                      color: 'white',
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    <LuCheck size={20} /> Aceptar Compra
                  </button>
                </Grid>
              </Box>
            )}

            {detail.decision === 'Aceptada' && (
              <Flex
                direction="column"
                alignItems="center"
                gap={2}
                py={4}
                bg="#eef7f6"
                borderRadius="lg"
                border="1px solid"
                borderColor="#86EFAC"
              >
                <Box color="#2a8a87">
                  <LuCircleCheck size={32} />
                </Box>
                <Text fontWeight="700" color="#1d6664">
                  Compra Aprobada
                </Text>
              </Flex>
            )}

            {detail.decision === 'Rechazada' && (
              <Box
                bg="#fae1db"
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor="#FCA5A5"
              >
                <Text fontWeight="700" color="#9a3020" mb={1}>
                  Carga Rechazada
                </Text>
                <Text fontSize="sm" color="#7F1D1D">
                  Motivo: {detail.rejectionReason ?? '—'}
                </Text>
              </Box>
            )}
          </VStack>
        )}
      </Modal>

      {/* Modal motivo de rechazo */}
      <Modal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Motivo de Rechazo"
      >
        <VStack gap={3} alignItems="stretch">
          <Text fontSize="sm" color={colors.textMuted}>
            Indique el motivo por el cual se rechaza la carga (visible en el historial).
          </Text>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            placeholder="Ej. No se llego a acuerdo de ley con el proveedor..."
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 6,
              border: '1px solid #e8dfd1',
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <Flex justifyContent="flex-end" gap={2}>
            <button
              type="button"
              onClick={() => setRejectModalOpen(false)}
              style={{
                padding: '8px 16px',
                background: 'white',
                color: '#4f4842',
                border: '1px solid #e8dfd1',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleRechazar}
              disabled={submitting || !rejectReason.trim()}
              style={{
                padding: '8px 16px',
                background: '#c8442f',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting || !rejectReason.trim() ? 0.5 : 1,
              }}
            >
              {submitting ? 'Rechazando...' : 'Confirmar Rechazo'}
            </button>
          </Flex>
        </VStack>
      </Modal>

      {/* Dialog éxito de aprobación */}
      <Modal
        open={successDialogOpen}
        onClose={() => {
          setSuccessDialogOpen(false)
          closeDetail()
        }}
        title=""
      >
        <Flex direction="column" alignItems="center" gap={3} py={6}>
          <Box color="#2a8a87">
            <LuCircleCheck size={56} />
          </Box>
          <Text fontWeight="700" fontSize="lg" color="#1d6664">
            Compra Aprobada
          </Text>
          <Text fontSize="sm" color={colors.textMuted} textAlign="center">
            El lote pasa al área de Balanza para pesaje oficial.
          </Text>
        </Flex>
      </Modal>
    </VStack>
  )
}
